port module Recapi exposing (main)

import Browser
import Html exposing (Html, text, form, input, button, div, span, nav, section, main_, header, details, summary, ul, li)
import Html.Events exposing (onClick, onInput, onSubmit, onCheck)
import Html.Attributes exposing (type_, placeholder, value, checked, class, classList, name, title)
import Json.Decode as D
import Json.Encode as E
import Task as Tsk
import File exposing (File)
import File.Select as Select
import File.Download as Download
import Bytes exposing (Bytes)
import Base64.Decode as B64D

import Icon as I


-- MAIN

main : Program () StateStore Event
main =
    Browser.element
        { init = init
        , view = view
        , update = update
        , subscriptions = subscriptions
        }


-- PORTS

port queries : E.Value -> Cmd msg
port results : (E.Value -> msg) -> Sub msg


-- SUBSCRIPTIONS

subscriptions : StateStore -> Sub Event
subscriptions stateStore =
    results decodeMessage

decodeMessage : E.Value -> Event
decodeMessage value =
    case D.decodeValue messageDecoder value of
        Ok message ->
            case message.signal of
                "tasks" ->
                    case D.decodeValue tasksDecoder message.payload of
                        Ok tasks ->
                            tasks |> Ok |> TasksRecieved |> SignalEvent

                        Err err ->
                            err |> Err |> TasksRecieved |> SignalEvent
                "TaskAdded" ->
                    case D.decodeValue taskDecoder message.payload of
                        Ok task ->
                            task |> Ok |> TaskAdded |> SignalEvent

                        Err err ->
                            err |> Err |> TaskAdded |> SignalEvent

                "TaskUpdated" ->
                    case D.decodeValue taskDecoder message.payload of
                        Ok task ->
                            task |> Ok |> TaskUpdated |> SignalEvent

                        Err err ->
                            err |> Err |> TaskUpdated |> SignalEvent

                "TaskStatusUpdated" ->
                    case D.decodeValue statusDecoder message.payload of
                        Ok newStatus ->
                            newStatus |> Ok |> TaskStatusUpdated |> SignalEvent

                        Err err ->
                            err |> Err |> TaskStatusUpdated |> SignalEvent

                "ScopeSwitched" ->
                    case D.decodeValue scopeDecoder message.payload of
                        Ok scope ->
                            scope |> Ok |> ScopeSwitched |> SignalEvent

                        Err err ->
                            err |> Err |> ScopeSwitched |> SignalEvent
                "DataExported" ->
                    case D.decodeValue D.string message.payload of
                        Ok dataStr ->
                            case B64D.decode B64D.bytes dataStr of
                                Ok data ->
                                    data |> Ok |> DataExported |> SignalEvent
                                Err err ->
                                    err |> Err |> DataExportedErr |> SignalEvent

                        Err err ->
                            err |> Err |> DataExported |> SignalEvent

                _ ->
                    SignalEvent (UnknownSignal (Err (D.Failure "Message signal uknowwn" value)))
        Err err ->
            SignalEvent (UnknownSignal (Err (err)))

-- OPERATIONS

type Operation
    = GetScope
    | CreateTask
    | CancelTask
    | CompleteTask
    | RestoreTask
    | UpdateTask
    | RecapitulateScope
    | ExportDataFromDB
    | ImportDataToDB

opName: Operation -> String
opName op =
    case op of
        GetScope -> "GetScope"
        CreateTask -> "CreateTask"
        CancelTask -> "CancelTask"
        CompleteTask -> "CompleteTask"
        RestoreTask -> "RestoreTask"
        UpdateTask -> "UpdateTask"
        RecapitulateScope -> "RecapitulateScope"
        ExportDataFromDB -> "ExportDataFromDB"
        ImportDataToDB -> "ImportDataToDB"

-- DECODERS

messageDecoder : D.Decoder Message
messageDecoder =
    D.map2 Message
        (D.field "signal" D.string)
        (D.field "payload" D.value)

scopeDecoder : D.Decoder Scope
scopeDecoder =
    D.map6 Scope
        (D.field "id" D.string)
        (D.field "stamp" D.string)
        (D.field "recapitulatedAt" (D.maybe D.string))
        (D.field "nextScopeId" (D.maybe D.string))
        (D.field "previousScopeId" (D.maybe D.string))
        (D.field "tasks" tasksDecoder)

tasksDecoder : D.Decoder (List Task)
tasksDecoder =
    D.list taskDecoder

taskDecoder : D.Decoder Task
taskDecoder =
    D.map4 Task
        (D.field "id" D.string)
        (D.field "note" D.string)
        (D.field "status" D.int |> D.map intToTaskStatus)
        (D.field "scopeId" D.string)

statusDecoder : D.Decoder UpdatedStatus
statusDecoder =
    D.map2 (Tuple.pair)
        (D.index 0 D.string)
        (D.index 1 D.int |> D.map intToTaskStatus)

intToTaskStatus : Int -> TaskStatus
intToTaskStatus n =
    case n of
        0 -> Active
        1 -> Completed
        2 -> Cancelled
        3 -> Recapitulated
        _ -> Active

-- ENCODERS

encodeTask : Task -> E.Value
encodeTask task =
    E.object
        [ ( "id", E.string task.id )
        , ( "note", E.string task.note )
        , ( "status", task.status |> taskStatusToInt |> E.int )
        , ( "scopeId", E.string task.scopeId )
        ]

encodeMessage : Operation -> E.Value -> E.Value
encodeMessage op payload =
    E.object [ ( "op", E.string <| opName op ), ( "payload", payload ) ]

encodeNote : TaskId -> String -> E.Value
encodeNote taskId note =
    E.object [ ( "id", E.string taskId ), ( "note", E.string note ) ]

taskStatusToInt : TaskStatus -> Int
taskStatusToInt status =
    case status of
        Active -> 0
        Completed -> 1
        Cancelled -> 2
        Recapitulated -> 3


-- MODEL

type alias Message =
    { signal : String
    , payload : D.Value
    }

type alias TaskId = String
type alias ScopeId = String
type alias Stamp = String
type alias UpdatedStatus = (TaskId, TaskStatus)

type alias Scope =
    { id : ScopeId
    , stamp : Stamp
    , recapitulatedAt : Maybe Stamp
    , nextScopeId : Maybe ScopeId
    , previousScopeId : Maybe ScopeId
    , tasks : List Task
    }

type TaskStatus
    = Active
    | Completed
    | Cancelled
    | Recapitulated

type alias Task =
    { id : TaskId
    , note : String
    , status : TaskStatus
    , scopeId : ScopeId
    }

type alias StateStore =
    { currentScopeId : Maybe ScopeId
    , currentStamp : Maybe Stamp
    , currentScopeInactive : Bool
    , tasks : List Task
    , nextScopeId : Maybe ScopeId
    , previousScopeId : Maybe ScopeId
    , newTaskNote : String
    , editTaskNote : String
    , taskInEditId : Maybe TaskId
    }

init : () -> ( StateStore, Cmd Event )
init _ =
    ( { currentScopeId = Nothing
      , currentStamp = Nothing
      , currentScopeInactive = False
      , tasks = []
      , nextScopeId = Nothing
      , previousScopeId = Nothing
      , newTaskNote = ""
      , editTaskNote = ""
      , taskInEditId = Nothing
      }
      , FetchScope Nothing |> Tsk.succeed |> Tsk.perform ActionEvent
    )


-- UPDATE

type alias Error =
    { message : String }

type Signal
    = TasksRecieved (Result D.Error (List Task))
    | TaskAdded (Result D.Error Task)
    | TaskStatusUpdated (Result D.Error UpdatedStatus)
    | TaskCancelled (Result D.Error TaskId)
    | TaskUpdated (Result D.Error Task)
    | ScopeSwitched (Result D.Error Scope)
    | UnknownSignal (Result D.Error ())
    | DataExported (Result D.Error Bytes)
    | DataExportedErr (Result B64D.Error ())

type Action
    = FetchScope (Maybe ScopeId)
    | InputTask String
    | AddTask
    | UpdateStatus TaskId Operation
    | ToggleEditMode TaskId
    | EditTask String
    | UpdateNote
    | SwitchScopeTo ScopeId
    | Recapitulate ScopeId
    | ExportData
    | SelectFile
    | UploadFile File
    | ImportData String

type Event
    = SignalEvent Signal
    | ActionEvent Action

resolveScopeId : Maybe ScopeId -> ScopeId
resolveScopeId maybeScopeId =
    case maybeScopeId of
        Just id -> id
        Nothing -> ""

resolveScopeInactive : Maybe String -> Bool
resolveScopeInactive recapitulatedAt =
    case recapitulatedAt of
        Just _ -> True
        Nothing -> False

removeFromList : String -> List Task -> List Task
removeFromList taskId tasks =
    List.filter (\task -> task.id /= taskId) tasks

updateTaskState : String -> TaskStatus -> List Task -> List Task
updateTaskState taskId status tasks =
    List.map
        (\task -> if task.id == taskId then { task | status = status } else task)
        tasks

update : Event -> StateStore -> ( StateStore, Cmd Event )
update evt stateStore =
    case evt of
        ActionEvent action ->
            case action of
                FetchScope scopeId ->
                    scopeId |> resolveScopeId |> E.string |> encodeMessage GetScope
                    |> queries
                    |> \cmd -> ( stateStore, cmd )

                AddTask ->
                    case String.trim stateStore.newTaskNote of
                        "" ->
                            ( stateStore, Cmd.none )

                        _ ->
                            (Task "new-id" stateStore.newTaskNote Active <| resolveScopeId stateStore.currentScopeId)
                            |> (\newTask -> encodeMessage CreateTask <| encodeTask newTask)
                            |> queries
                            |> (\cmd -> ( { stateStore | newTaskNote = "" }, cmd ))

                InputTask note ->
                    { stateStore | newTaskNote = note }
                        |> \newState -> ( newState, Cmd.none )

                UpdateStatus taskId op ->
                    (stateStore, (encodeMessage op <| E.string taskId) |> queries)

                ToggleEditMode taskId ->
                    let
                        maybeTask =
                            List.filter (\task -> task.id == taskId) stateStore.tasks
                                |> List.head
                    in
                    case maybeTask of
                        Just task ->
                            ( { stateStore
                              | taskInEditId = Just taskId
                              , editTaskNote = task.note
                              }
                            , Cmd.none)
                        Nothing ->
                            ( { stateStore
                            | taskInEditId = Nothing
                            , editTaskNote = ""
                            }
                            , Cmd.none)

                EditTask note ->
                    ({ stateStore | editTaskNote = note }, Cmd.none )

                UpdateNote ->
                    let
                        taskId =
                            case stateStore.taskInEditId of
                                Just id -> id
                                Nothing -> ""
                        note = stateStore.editTaskNote
                    in
                    (stateStore, (encodeMessage UpdateTask <| encodeNote taskId note) |> queries)


                SwitchScopeTo scopeId ->
                    (stateStore, (encodeMessage GetScope <| E.string scopeId) |> queries)

                Recapitulate scopeId ->
                    (stateStore, (encodeMessage RecapitulateScope <| E.string scopeId) |> queries)

                ExportData ->
                    (stateStore, (encodeMessage ExportDataFromDB <| E.null) |> queries)

                SelectFile ->
                    (stateStore, Select.file [] UploadFile |> Cmd.map ActionEvent)

                UploadFile file ->
                    (stateStore, Tsk.perform ImportData (File.toUrl file) |> Cmd.map ActionEvent)

                ImportData fileData ->
                    ( stateStore, (encodeMessage ImportDataToDB <| E.string <| fileData ) |> queries )


        SignalEvent signal ->
            case signal of
                TasksRecieved (Ok message) ->
                    ( { stateStore | tasks = message }, Cmd.none )

                TaskAdded (Ok task) ->
                    ( { stateStore | tasks = stateStore.tasks ++ [task] }, Cmd.none )

                TaskStatusUpdated (Ok (taskId, state)) ->
                    ( { stateStore | tasks = updateTaskState taskId state stateStore.tasks }, Cmd.none )

                TaskCancelled (Ok taskId) ->
                    ( { stateStore | tasks = updateTaskState taskId Cancelled stateStore.tasks }, Cmd.none )

                TaskUpdated (Ok updatedTask) ->
                    let
                        updatedTasks =
                            List.map
                                (\task -> if task.id == updatedTask.id then updatedTask else task)
                                stateStore.tasks
                    in
                    ( { stateStore
                      | tasks = updatedTasks
                      , taskInEditId = Nothing
                      , editTaskNote = ""
                      }, Cmd.none )
                ScopeSwitched (Ok scope) ->
                    ( { stateStore
                      | currentScopeId = Just scope.id
                      , currentStamp = Just scope.stamp
                      , currentScopeInactive = resolveScopeInactive scope.recapitulatedAt
                      , previousScopeId = scope.previousScopeId
                      , nextScopeId = scope.nextScopeId
                      , tasks = scope.tasks
                      }, Cmd.none )
                DataExported (Ok data) ->
                    ( stateStore, Download.bytes "recapi_export.sqlite3" "application/octet-stream" data )
                TasksRecieved err ->
                    ( stateStore, Cmd.none )
                TaskAdded err ->
                    ( stateStore, Cmd.none )
                TaskStatusUpdated err ->
                    ( stateStore, Cmd.none )
                TaskCancelled err ->
                    ( stateStore, Cmd.none )
                TaskUpdated err ->
                    ( stateStore, Cmd.none )
                ScopeSwitched err ->
                    ( stateStore, Cmd.none )
                DataExported err ->
                    ( stateStore, Cmd.none )
                DataExportedErr err ->
                    ( stateStore, Cmd.none )
                UnknownSignal _ ->
                    ( stateStore, Cmd.none )


-- VIEW

taskButtonClass : String
taskButtonClass =
    "btn btn-primary btn-sm btn-ghost btn-circle invisible group-hover:visible"

taskButton : Action -> Html Event -> String -> Html Event
taskButton action icon classes =
    button
        [ onClick (ActionEvent action)
        , classList
            [ ( taskButtonClass, True )
            , ( classes, True )
            ]
        ] [ icon ]

navButtonView : String -> Maybe ScopeId -> Bool -> ScopeId -> Html Event
navButtonView label maybeScopeId isForward currentScopeId =
    case maybeScopeId of
        Just scopeId ->
            let
                icon =
                    if isForward then
                        I.iconNavRight
                    else
                        I.iconNavLeft
                titleText =
                    if isForward then
                        "Next"
                    else
                        "Previous"
            in
            button
                [ onClick (SwitchScopeTo scopeId |> ActionEvent)
                , class "join-item btn btn-primary btn-lg"
                , title titleText
                ] [ icon ]
        Nothing ->
            if isForward then
                button [ onClick (Recapitulate currentScopeId |> ActionEvent)
                       , class "join-item btn btn-secondary btn-lg"
                       , title "Recapitulate"
                       ] [ I.iconNavRecap ]
            else
                button [ class "join-item btn btn-primary btn-lg w-16"] [ text "" ]

navView : StateStore -> Html Event
navView stateStore =
    let
        scopeTitle =
            case stateStore.currentStamp of
                Just stamp -> stamp
                Nothing -> "No Scope"
        prev = stateStore.previousScopeId
        next = stateStore.nextScopeId
        curr =
            case stateStore.currentScopeId of
                Just id -> id
                Nothing -> ""
    in
    header [ class "flex justify-between items-center navbar p-0" ]
        [ infoView
        , nav [ class "join"]
            [ navButtonView "<<" prev False curr
            , button [ class "join-item btn btn-primary btn-lg" ] [ text scopeTitle ]
            , navButtonView ">>" next True curr
            ]
        , settingsMenu
        ]

infoView : Html msg
infoView =
    details [ class "dropdown", title "About Recapitulation" ]
        [ summary [ class "btn btn-ghost btn-circle btn-lg" ] [ I.iconInfo ]
        , div [ class "dropdown-content card w-96 p-4 bg-base-100 shadow-lg mt-2" ]
            [ div [ class "card-body" ]
                [ header [ class "card-title" ] [ text "Recapitulation" ]
                , aboutRecapiView
                ]
            ]
        ]

aboutRecapiView : Html msg
aboutRecapiView =
    section [ class "text-lg" ]
        [ text "Recapitulation is a simple task management application designed to help you keep track of your tasks efficiently."
        , Html.br [] []
        , text "Features include adding, editing, completing, and cancelling tasks, as well as navigating between different scopes of tasks."
        ]

settingsMenu : Html Event
settingsMenu =
    details [ class "dropdown dropdown-end", title "Settings" ]
        [ summary [ class "btn btn-ghost btn-circle btn-lg" ] [ I.iconSettings ]
        , ul [ class "dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52 mt-2" ]
            [ li [] [ button [ onClick (ActionEvent ExportData), class "btn btn-ghost w-full justify-start" ] [ I.iconExport, text " Export Data" ] ]
            , li [] [ button [ onClick (ActionEvent SelectFile), class "btn btn-ghost w-full justify-start" ] [ I.iconImport, text " Import Data" ] ]
            ]
        ]

tickboxView : Task -> Html Event
tickboxView task =
    let
        icon = case task.status of
                Recapitulated -> text ""
                Active -> I.iconTakskComplete
                _ -> I.iconTaskRestore
        op = case task.status of
                Active -> CompleteTask
                _ -> RestoreTask
    in
    case task.status of
        Recapitulated ->
            div[ class "size-lg"] [ text "" ]
        _ ->
            div [ class "size-lg" ] [
                taskButton (UpdateStatus task.id op) icon ""
            ]


editView : Task -> Maybe TaskId -> String  -> Html Event
editView task inEditId note =
    if task.status == Active && Just task.id == inEditId then
        form [ onSubmit (ActionEvent UpdateNote)
             , class "flex flex-row align-center"
             ] [ button
                    [ type_ "button"
                    , onClick ( ToggleEditMode "" |> ActionEvent )
                    , class "btn btn-secondary btn-sm mr-2 outline-none"
                    ] [ I.iconTasksDiscard ]
                , input
                    [ type_ "text"
                    , value note
                    , onInput (EditTask >> ActionEvent)
                    , class "w-full input flex-1 outline-none text-lg h-auto"
                    ] []
                , button
                    [ type_ "submit"
                    , class "btn btn-secondary btn-sm ml-2 outline-none"
                    ] [ I.iconTasksSave ]
            ]
    else
        span [ class "text-lg"] [
            text task.note
        ]


taskView : Maybe TaskId -> String -> Bool -> Task -> Html Event
taskView inEditId note inactive task =
    let
        showButtons = task.status == Active && Just task.id /= inEditId
        editButton =
            if showButtons then
                taskButton (ToggleEditMode task.id) I.iconTasksEdit "mr-1"
            else
                text ""

        cancelButton =
            if showButtons then
                taskButton (UpdateStatus task.id CancelTask) I.iconTasksCancel ""
            else
                text ""

        tickboxButton =
            if Just task.id /= inEditId then
                tickboxView task
            else
                div[ class "size-lg"] [ text "" ]
    in
    div [ classList
            [ ( "grid grid-cols-[32px_1fr_32px_32px]", True )
            , ( "group bg-size-[5px] bg-bottom bg-repeat-x hover:bg-(image:--bg-dots)", not inactive )
            ]
        ]
        [ tickboxButton
        , div [ classList
            [ ( "px-1 pt-[0.15rem] flex-1", True )
            , ( "text-primary", task.status == Completed )
            , ( "text-primary line-through", task.status == Cancelled )
            , ( "line-through", task.status == Recapitulated )
            ] ] [
            editView task inEditId note
        ]
        , div [ class "size-lg" ] [
            editButton
        ]
        , div [ class "size-lg" ] [
            cancelButton
        ]
    ]

tasksView : List Task -> Maybe TaskId -> String -> Bool -> Html Event
tasksView tasks inEditId note inactive =
    section []
        (List.map (taskView inEditId note inactive) tasks)

addTaskView : String -> Html Event
addTaskView newTaskNote =
    section [ class "grid grid-cols-[32px_1fr_64px] mt-5 px-2" ]
        [ form [ onSubmit (ActionEvent AddTask)
               , class "col-start-2 col-end-3"
               ]
            [ div [ class "flex flex-row align-center" ]
                [ input
                    [ type_ "text"
                    , placeholder "what now?"
                    , value newTaskNote
                    , onInput (InputTask >> ActionEvent)
                    , class "w-full input flex-1 outline-none text-lg"
                    ] []
                , button
                    [ type_ "submit"
                    , class "btn btn-primary ml-2 outline-none"
                    ] [ I.iconFormAdd ]
                ]
            ]
        ]

view : StateStore -> Html Event
view stateStore =
    let
        tasks = stateStore.tasks
        inEditId = stateStore.taskInEditId
        note = stateStore.editTaskNote
        inactive = stateStore.currentScopeInactive
    in
    main_
        [class "mx-auto max-w-2xl"]
        [ navView stateStore
        , tasksView tasks inEditId note inactive
        , case inactive of
            True -> section [] []
            False -> addTaskView stateStore.newTaskNote
        ]
